import React, { FC, useEffect, useState } from 'react';
import { differenceInSeconds } from 'date-fns';

import { Box, CloseModalButton, ExternalLeft24, IconButton, Stack, Typography } from 'Modules/ui3';
import { useTranslation } from 'Modules/core/i18n';
import { messagingActions, useModalContext } from 'Modules/core/messaging';
import { useAction } from 'Modules/core/redux';

import { ConfimationCodeProps, SendCodeProps } from './types';
import { FeedbackModalComponentProps, FeedbackModalStage } from '../FeedbackModal/types';
import { SEND_CODE_DELAY_SECONDS, useCodeTime } from '../../state/feedback';
import { CodeForm } from '../ConfirmationCodeForm';


const SendCode: FC<SendCodeProps> = React.memo(({ t, email, onRequestCode }) => {
    const codeTime = useCodeTime(email);
    const [timer, setTimer] = useState(0);
    const disableSend = timer > 0;

    useEffect(() => {
        const timeDeltaSeconds = differenceInSeconds(new Date().getTime(), codeTime);

        if (SEND_CODE_DELAY_SECONDS > timeDeltaSeconds) {
            setTimer(SEND_CODE_DELAY_SECONDS - timeDeltaSeconds);
            const countDown = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);

            return () => clearInterval(countDown);
        }
        return null;
    }, [codeTime]);

    return (
        <Typography
            component='p'
            variant='regularBody2'
            color={disableSend ? 'grayscale.shade90' : 'general.primary50'}
            textAlign='center'
            {...disableSend ? {} : {
                onClick: onRequestCode,
                sx: {
                    cursor: 'pointer',
                },
            }}
        >
            {t(disableSend ? 'sendCodeTimer' : 'sendCode', { timer })}
        </Typography>
    );
});

export const ConfimationCode: FC<ConfimationCodeProps> = ({
    email, onRequestCode, onCodeEnter, errorMessage,
}) => {
    const { t } = useTranslation('confimationCode');
    const { modal: { hide, id } } = useModalContext();

    const goBack = useAction(() => messagingActions.updateModalProps({
        id,
        props: {
            stage: FeedbackModalStage.INIT,
        } as FeedbackModalComponentProps,
    }), [id]);

    return (
        <Box maxWidth='480px'>
            <Stack direction='row' p={4} justifyContent='space-between'>
                <IconButton size='small' onClick={goBack} variant='stroke' isRound>
                    <ExternalLeft24 />
                </IconButton>
                <CloseModalButton onClick={hide} size='small' absolute={false} />
            </Stack>
            <Box p={8} pt={0}>
                <Typography component='h1' variant='boldTitle5' color='grayscale.shade95'>
                    {t('title')}
                </Typography>
                <Typography component='h3' variant='regularCaption1' color='grayscale.shade90' mt={3}>
                    {t('helper', { email })}
                </Typography>
                <Box my={8}>
                    {errorMessage && (
                        <Typography
                            component='p'
                            textAlign='center'
                            variant='regularCaption1'
                            color='infoCustom.red50'
                            mb={4}
                        >
                            {errorMessage}
                        </Typography>
                    )}
                    <CodeForm onCodeEnter={onCodeEnter} isError={!!errorMessage} />
                </Box>
                <SendCode t={t} email={email} onRequestCode={onRequestCode} />
            </Box>
        </Box>
    );
};
